import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  TableSortLabel,
  Tabs,
  Tab,
  Typography,
  TextField,
} from '@mui/material';
import { ContentTileComponent } from '../../Components/ContentTitle';
import useGentian from '../../core/hooks/useGentian';
import { useUser } from '../../core/hooks';
import { calculateHestiCost } from './logic/calculate-cost';
import { formatDate } from './logic/format-date';
import { isReseller } from '../../Utils/account.utils';
import { calculatePriceByType, PricingType } from '../../Utils/pricing.utils';

const MyDashboard = () => {
  const { useUsage, useAllProjects, useProjects } = useGentian();
  const { hasPermission } = useUser();
  const { data: accountData } = useGentian().useAccount();
  const isResellerAccount = isReseller(accountData);
  const standardPricingType: PricingType = isResellerAccount ? 'resellers' : 'default';
  const trueSagePricingType: PricingType = isResellerAccount ? 'truesageResellers' : 'truesage';

  // State declarations
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState<{ by: string; dir: 'asc' | 'desc' }>({ by: 'orderDate', dir: 'desc' });
  const [activeTab, setActiveTab] = useState(0);
  const [start, setStart] = useState(0);
  const [projectPage, setProjectPage] = useState(0);
  const [projectRowsPerPage, setProjectRowsPerPage] = useState(20);
  const [searchQuery, setSearchQuery] = useState('');

  // Data fetching
  const {
    data: usageDataRaw,
    isLoading,
    isError,
    refetch,
  } = useUsage({
    start,
    limit: rowsPerPage,
    sort: order.by,
    sortDirection: order.dir,
  });

  const { data: projectsWithResult } = useAllProjects({ status: 'has-result' });
  const { data: projectsWithoutResult } = useAllProjects({ status: 'no-result' });
  const { data: userProjects } = useProjects();

  // Calculate project statistics
  const totalProjectsCount = userProjects?.length || 0;
  const projectsLastWeek = useMemo(() => {
    if (!userProjects) return 0;
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    return userProjects.filter((project) => {
      const projectDate = new Date(project.requestDate);
      return projectDate >= oneWeekAgo;
    }).length;
  }, [userProjects]);

  // Project data processing
  const allProjects = useMemo(
    () => [...(projectsWithResult || []), ...(projectsWithoutResult || [])],
    [projectsWithResult, projectsWithoutResult],
  );

  const trueSightProjects = useMemo(
    () =>
      allProjects.filter(
        (project) => Array.isArray(project.products) && project.products.some((p) => p.name === 'True Sight'),
      ),
    [allProjects],
  );

  const trueSageProjects = useMemo(
    () =>
      allProjects.filter(
        (project) => Array.isArray(project.products) && project.products.some((p) => p.name === 'True Sage'),
      ),
    [allProjects],
  );

  const trueOracleProjects = useMemo(
    () =>
      allProjects.filter(
        (project) =>
          Array.isArray(project.products) && project.products.some((p) => p.name === 'True Oracle'),
      ),
    [allProjects],
  );

  // API tab data processing
  const apiUsageRows = useMemo(() => {
    if (!usageDataRaw?.items) return [];

    const filteredItems = usageDataRaw.items.filter((item) => {
      const idBeforeAt = item.id?.split('@')[0] || '';
      return idBeforeAt === item.uuid;
    });

    return filteredItems.map((item) => {
      const project = allProjects.find((project) => project._id === item.uuid);
      const clientName = item.clientName || project?.customerName;
      const normalizedProductType = item.product.toLowerCase().replace(/\s+/g, '');
      const pricingType: PricingType =
        normalizedProductType === 'default'
          ? standardPricingType
          : normalizedProductType === 'truesage'
            ? trueSagePricingType
            : (normalizedProductType as PricingType);
      const cost =
        clientName === 'Viability (Hesti)'
          ? calculateHestiCost(item.totalArea)
          : calculatePriceByType(item.totalArea, pricingType);

      return {
        id: item.uuid,
        product: item.product,
        clientName,
        orderDate: item.orderDate,
        totalArea: item.totalArea,
        biodiversityUnits: item.biodiversityUnits,
        status: item.status,
        projectName: item.projectName ? item.projectName : item.uuid,
        cost,
      };
    });
  }, [usageDataRaw, allProjects, standardPricingType, trueSagePricingType]);

  type DashboardTab = {
    key: string;
    label: string;
    projects?: typeof trueSightProjects;
    isApi?: boolean;
  };

  const productTabs = useMemo<DashboardTab[]>(() => {
    const tabs: DashboardTab[] = [
      { key: 'true_sight', label: 'True Sight', projects: trueSightProjects },
      { key: 'true_sage', label: 'True Sage', projects: trueSageProjects },
    ];

    if (!isResellerAccount) {
      tabs.push({ key: 'true_oracle', label: 'True Oracle', projects: trueOracleProjects });
    }

    return tabs;
  }, [isResellerAccount, trueSightProjects, trueSageProjects, trueOracleProjects]);

  const tabs = useMemo<DashboardTab[]>(() => {
    const baseTabs: DashboardTab[] = [...productTabs];
    if (hasPermission('ADMIN:usage')) {
      baseTabs.push({ key: 'api', label: 'API', isApi: true });
    }
    return baseTabs;
  }, [productTabs, hasPermission]);

  const currentTab = tabs[activeTab];
  const isApiTab = currentTab?.isApi === true;

  useEffect(() => {
    if (activeTab >= tabs.length) {
      setActiveTab(0);
    }
  }, [activeTab, tabs.length]);

  // Projects for current tab
  const projectsForTab = useMemo(() => {
    if (isApiTab || !currentTab || !('projects' in currentTab)) return [];
    return currentTab.projects || [];
  }, [currentTab, isApiTab]);

  const filteredProjectRows = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return projectsForTab;

    return projectsForTab.filter((project) => {
      const productName = project?.products?.[0]?.name || '';
      const requestDateText = project?.requestDate ? new Date(project.requestDate).toLocaleDateString() : '';
      const costText =
        productName === 'True Sage'
          ? calculatePriceByType(project.totalArea || 0, trueSagePricingType).toFixed(2)
          : productName === 'True Oracle'
            ? 'N/A'
            : calculatePriceByType(project.totalArea || 0, standardPricingType).toFixed(2);

      const haystack = [
        project?._id,
        productName,
        project?.name,
        project?.customerName,
        requestDateText,
        project?.totalArea,
        project?.status,
        costText,
        project?.resellerCustomerName,
        project?.resellerExternalProjectId,
        project?.resellerNotes,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [projectsForTab, searchQuery, standardPricingType, trueSagePricingType]);

  const filteredApiRows = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return apiUsageRows;

    return apiUsageRows.filter((row) => {
      const haystack = [
        row?.id,
        row?.product,
        row?.projectName,
        row?.clientName,
        row?.orderDate ? formatDate(row.orderDate) : '',
        row?.totalArea,
        row?.biodiversityUnits,
        row?.status,
        row?.cost?.toFixed?.(2) ?? row?.cost,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [apiUsageRows, searchQuery]);

  // Paginated project rows
  const paginatedProjectRows = useMemo(() => {
    const startIndex = projectPage * projectRowsPerPage;
    return filteredProjectRows.slice(startIndex, startIndex + projectRowsPerPage);
  }, [filteredProjectRows, projectPage, projectRowsPerPage]);

  // Table columns
  const tableColumns = [
    { key: 'id', label: 'ID', sortable: true },
    { key: 'product', label: 'Product', sortable: true },
    { key: 'projectName', label: 'Project Name/UUID', sortable: true },
    { key: 'clientName', label: 'Client Name', sortable: true },
    ...(isResellerAccount
      ? [
          { key: 'resellerCustomerName', label: 'Reseller Customer Name', sortable: true },
          { key: 'resellerExternalProjectId', label: 'Reseller External Project ID', sortable: true },
          { key: 'resellerNotes', label: 'Reseller Notes', sortable: true },
        ]
      : []),
    { key: 'orderDate', label: 'Order Date', sortable: true },
    { key: 'totalArea', label: 'Total Area (ha)', sortable: true },
    { key: 'status', label: 'Status', sortable: true },
    { key: 'cost', label: 'Cost (GBP)', sortable: true },
  ];

  const apiColumns = [
    { key: 'id', label: 'ID', sortable: true },
    { key: 'product', label: 'Product', sortable: true },
    { key: 'projectName', label: 'Project Name', sortable: true },
    { key: 'clientName', label: ' Client Name', sortable: true },
    { key: 'orderDate', label: 'Order Date', sortable: true },
    { key: 'totalArea', label: 'Total Area (ha)', sortable: true },
    { key: 'biodiversityUnits', label: 'Biodiversity Units', sortable: true },
    { key: 'status', label: 'Status', sortable: true },
    { key: 'cost', label: 'Cost (GBP)', sortable: true },
  ];

  // Handle sorting
  const handleRequestSort = (property: string) => {
    const isAsc = order.by === property && order.dir === 'asc';
    setOrder({ by: property, dir: isAsc ? 'desc' : 'asc' });
    refetch();
  };

  // Handle page change
  const handleChangePage = (_: unknown, newPage: number) => {
    const nextStart = newPage * rowsPerPage;
    setStart(nextStart);
    setPage(newPage);
    refetch();
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10) || 0);
    setStart(0);
    setPage(0);
    refetch();
  };

  // Handle project page change
  const handleProjectChangePage = (_: unknown, newPage: number) => {
    setProjectPage(newPage);
  };

  // Handle project rows per page change
  const handleProjectChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setProjectRowsPerPage(parseInt(event.target.value, 10) || 0);
    setProjectPage(0);
  };

  const createSortHandler = (property: string) => () => {
    handleRequestSort(property);
  };

  const getEncouragementMessage = () => {
    if (totalProjectsCount === 0) {
      return "Let's get started with your first survey!";
    } else if (totalProjectsCount < 5) {
      return "Let's keep surveying that land!";
    } else {
      return "You're on a roll! Let's see if there's more land to survey.";
    }
  };

  // Loading and error states
  const LoadingState = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <CircularProgress />
    </Box>
  );

  const ErrorState = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <Alert severity="error">Error fetching data</Alert>
    </Box>
  );

  if (isLoading) return <LoadingState />;
  if (isError) return <ErrorState />;

  return (
    <Box sx={{ padding: '20px', display: 'flex', flexDirection: 'column' }}>
      <ContentTileComponent className="hide-on-fullscreen">
        <Box sx={{ pl: 'var(--content-spacing-narrow)' }}>
          {hasPermission('ADMIN:usage') ? 'Admin Dashboard' : 'My Dashboard'}
        </Box>
      </ContentTileComponent>
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box
          sx={{
            flex: 1,
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
            Total Projects
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            {totalProjectsCount}
          </Typography>
        </Box>
        <Box
          sx={{
            flex: 1,
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
            Projects in Last Week
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
            {projectsLastWeek}
          </Typography>
          <Typography variant="body1" sx={{ mt: 1, fontStyle: 'italic' }}>
            {getEncouragementMessage()}
          </Typography>
        </Box>
      </Box>

      <Tabs
        value={activeTab}
        onChange={(_, v) => {
          setActiveTab(v);
          setPage(0);
          setProjectPage(0);
        }}
      >
        {tabs.map((tab) => (
          <Tab key={tab.key} label={tab.label} />
        ))}
      </Tabs>

      <TextField
        value={searchQuery}
        onChange={(e) => {
          setSearchQuery(e.target.value);
          setProjectPage(0);
          setPage(0);
        }}
        placeholder="Search across all fields"
        variant="standard"
        sx={{ width: '256px', mt: 2 }}
      />

      {!isApiTab && hasPermission('ADMIN:usage') && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              const headers = [
                'ID',
                'Product',
                'Project Name',
                'Client Name',
                ...(isResellerAccount
                  ? ['Reseller Customer Name', 'Reseller External Project ID', 'Reseller Notes']
                  : []),
                'Order Date',
                'Total Area (ha)',
                'Status',
                'Cost (GBP)',
              ];

              const escapeCsv = (value: unknown) => {
                if (value === null || value === undefined) return '';
                const str = String(value);
                if (/[\",\\n]/.test(str)) return `\"${str.replace(/\"/g, '\"\"')}\"`;
                return str;
              };

              const rows = filteredProjectRows.map((project) => {
                const productName = project?.products?.[0]?.name || 'N/A';
                const cost =
                  productName === 'True Sage'
                    ? calculatePriceByType(project.totalArea || 0, trueSagePricingType).toFixed(2)
                    : productName === 'True Oracle'
                      ? 'N/A'
                      : calculatePriceByType(project.totalArea || 0, standardPricingType).toFixed(2);

                return [
                  project?._id?.toString() || '',
                  productName,
                  project?.name || '',
                  project?.customerName || '',
                  ...(isResellerAccount
                    ? [
                        project?.resellerCustomerName || '',
                        project?.resellerExternalProjectId || '',
                        project?.resellerNotes || '',
                      ]
                    : []),
                  project?.requestDate ? new Date(project.requestDate).toLocaleDateString() : '',
                  project?.totalArea ?? '',
                  project?.status || '',
                  cost,
                ];
              });

              const csv = [headers, ...rows].map((row) => row.map(escapeCsv).join(',')).join('\n');

              const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `orders_export_${new Date().toISOString().slice(0, 10)}.csv`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);
            }}
          >
            Export CSV
          </Button>
        </Box>
      )}

      {!isApiTab ? (
        // Render product tabs (True Sight, Sage, Oracle)
        <>
          <TableContainer component={Paper} sx={{ flexGrow: 1 }}>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  {tableColumns.map((column) => (
                    <TableCell key={column.key}>
                      {column.sortable ? (
                        <TableSortLabel
                          active={order.by === column.key}
                          direction={order.by === column.key ? order.dir : 'asc'}
                          onClick={createSortHandler(column.key)}
                        >
                          {column.label}
                        </TableSortLabel>
                      ) : (
                        column.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedProjectRows.map((project) => (
                  <TableRow key={project._id}>
                    <TableCell>{project._id?.toString().slice(-6)}</TableCell>
                    <TableCell>{project.products?.[0]?.name || 'N/A'}</TableCell>
                    <TableCell>{project.name || 'N/A'}</TableCell>
                    <TableCell>{project.customerName || 'N/A'}</TableCell>
                    {isResellerAccount && (
                      <>
                        <TableCell>{project.resellerCustomerName || 'N/A'}</TableCell>
                        <TableCell>{project.resellerExternalProjectId || 'N/A'}</TableCell>
                        <TableCell>{project.resellerNotes || 'N/A'}</TableCell>
                      </>
                    )}
                    <TableCell>
                      {project?.requestDate ? new Date(project.requestDate).toLocaleDateString() : 'N/A'}
                    </TableCell>
                    <TableCell>{project.totalArea ?? 'N/A'}</TableCell>
                    <TableCell>{project.status || 'N/A'}</TableCell>
                    <TableCell>
                      {project?.products?.[0]?.name === 'True Sage'
                        ? `GBP ${calculatePriceByType(project.totalArea || 0, trueSagePricingType).toFixed(2)}`
                        : project?.products?.[0]?.name === 'True Oracle'
                          ? 'N/A'
                          : `GBP ${calculatePriceByType(project.totalArea || 0, standardPricingType).toFixed(
                              2,
                            )}`}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 20, 50, 100]}
            component="div"
            count={filteredProjectRows.length}
            rowsPerPage={projectRowsPerPage}
            page={projectPage}
            onPageChange={handleProjectChangePage}
            onRowsPerPageChange={handleProjectChangeRowsPerPage}
            sx={{ mt: 2 }}
          />
        </>
      ) : hasPermission('ADMIN:usage') ? (
        // Render API tab if authorized
        <>
          <TableContainer component={Paper} sx={{ flexGrow: 1 }}>
            <Table sx={{ minWidth: 650 }} aria-label="api table">
              <TableHead>
                <TableRow>
                  {apiColumns.map((column) => (
                    <TableCell key={column.key}>
                      {column.sortable ? (
                        <TableSortLabel
                          active={order.by === column.key}
                          direction={order.by === column.key ? order.dir : 'asc'}
                          onClick={createSortHandler(column.key)}
                        >
                          {column.label}
                        </TableSortLabel>
                      ) : (
                        column.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredApiRows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row?.id?.toString().slice(-6)}</TableCell>
                    <TableCell>{row.product}</TableCell>
                    <TableCell>{row.projectName}</TableCell>
                    <TableCell>{row.clientName}</TableCell>
                    <TableCell>{formatDate(row.orderDate)}</TableCell>
                    <TableCell>{row.totalArea}</TableCell>
                    <TableCell>{row.biodiversityUnits}</TableCell>
                    <TableCell>{row.status}</TableCell>
                    <TableCell>GBP {row.cost.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 20, 50, 100]}
            component="div"
            count={filteredApiRows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ mt: 2 }}
          />
        </>
      ) : (
        // Show message if unauthorized for API tab
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6">You don&apos;t have permission to view API usage data.</Typography>
        </Box>
      )}
    </Box>
  );
};

export default MyDashboard;
